import React from 'react';
import { CheckCircle, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { useLiveEvent } from './LiveEventContext';

const formatCountdownTime = (seconds) => {
  if (!seconds && seconds !== 0) return '00:00';
  const m = Math.floor(Math.abs(seconds) / 60);
  const s = Math.floor(Math.abs(seconds) % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const ProtestSection = () => {
  const {
    t, language, currentSession, currentStatus, protestPhase,
    protestCountdown, handleCloseEvent
  } = useLiveEvent();

  if (!currentStatus || currentStatus.current_passage || !currentStatus.session) return null;
  if (currentStatus.session.status === 'closed') {
    return (
      <div className="text-center py-10 space-y-6" data-testid="event-terminated-section">
        <div className="bg-gradient-to-br from-purple-900/60 to-indigo-900/60 border-2 border-purple-500/50 rounded-2xl p-10 max-w-2xl mx-auto">
          <Lock size={56} className="mx-auto text-purple-400 mb-4" />
          <h2 className="text-3xl font-black text-white mb-3">{t('eventTerminated') || 'Event Completely Terminated!'}</h2>
          <p className="text-purple-300 text-lg">{t('resultsAreFinal') || 'Results are final'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center py-6 space-y-4">
        <CheckCircle size={40} className="mx-auto text-green-400 mb-2" />
        <p className="text-green-200 font-semibold text-lg">{t('allPassagesCompleted') || 'All passages completed!'}</p>

        {/* Time Before Protest */}
        {protestPhase === 'before_protest' && (
          <div className="bg-yellow-900/30 border border-yellow-600/40 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-yellow-200 font-semibold text-base mb-2">{t('timeBeforeProtest') || 'Time Before Protest'}</p>
            <p className="text-yellow-400 font-mono text-3xl font-bold" data-testid="before-protest-countdown">
              {formatCountdownTime(protestCountdown)}
            </p>
          </div>
        )}

        {/* Protest Active */}
        {protestPhase === 'protest_active' && (
          <div className="bg-red-900/30 border border-red-600/40 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-red-200 font-semibold text-base mb-2">{t('protestPeriodInProgress') || 'Protest Period in Progress'}</p>
            <p className="text-red-400 font-mono text-3xl font-bold animate-pulse" data-testid="protest-active-countdown">
              {formatCountdownTime(protestCountdown)}
            </p>
          </div>
        )}

        {/* Protest Ended */}
        {protestPhase === 'protest_ended' && (
          <div className="bg-green-900/30 border border-green-600/40 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-green-200 font-semibold text-lg">{t('protestPeriodEnded') || 'Protest period ended. Results are final.'}</p>
          </div>
        )}

        {/* Terminate Event Button */}
        {(protestPhase === 'protest_active' || protestPhase === 'protest_ended') && (
          <Button
            onClick={handleCloseEvent}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-lg px-8 py-3 shadow-lg shadow-purple-900/50"
            data-testid="terminate-event-btn"
          >
            <Lock size={20} className="mr-2" />
            {t('terminateEvent') || 'Terminate Event'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProtestSection;
